/**
 * Internationalisation.
 *
 * One locale ships — `es-PE` — but every visible string goes through a key
 * from day one. Retrofitting i18n across an admin-heavy application later is
 * a multi-week mechanical refactor; doing it now costs a `t()` call.
 *
 * Keys are English (they are identifiers); values are Spanish (they are
 * content). See CLAUDE.md for the boundary that matters: UI chrome is
 * translated here, and domain content comes from the API — a label an
 * administrator can edit does not belong in a bundle.
 *
 * One namespace per feature. They are loaded eagerly because there are three
 * of them; when that stops being true, lazy-load rather than merging them.
 */
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import { isDev } from '@/config/env';

import auth from './locales/es-PE/auth.json';
import common from './locales/es-PE/common.json';
import employees from './locales/es-PE/employees.json';
import home from './locales/es-PE/home.json';
import productObservations from './locales/es-PE/productObservations.json';
import sunatCommunicationBatches from './locales/es-PE/sunatCommunicationBatches.json';
import validation from './locales/es-PE/validation.json';

export const defaultNS = 'common';

export const resources = {
  'es-PE': {
    common,
    auth,
    home,
    validation,
    employees,
    productObservations,
    sunatCommunicationBatches,
  },
} as const;

void i18n.use(initReactI18next).init({
  resources,
  lng: 'es-PE',
  fallbackLng: 'es-PE',
  defaultNS,
  ns: [
    'common',
    'auth',
    'home',
    'validation',
    'employees',
    'productObservations',
    'sunatCommunicationBatches',
  ],
  interpolation: { escapeValue: false }, // React already escapes
  returnNull: false,
  // A missing key must be loud in development and harmless in production.
  saveMissing: false,
  parseMissingKeyHandler: (key) => (isDev ? `⚠️ ${key}` : key),
});

export default i18n;
